import React from 'react';
import { Dimensions, ScrollView, StyleSheet, Text, View } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { toHtmlTextStyle } from '../components/general/toHtmlTextStyle';

const WIDTH = Dimensions.get('window').width;
const PAGE_HORIZONTAL_PADDING = WIDTH >= 1200 ? 100 : 24;
const IS_WIDE_LAYOUT = WIDTH >= 1080;

const reasons = [
    {
        icon: 'map-marker-path',
        title: 'National expansion opportunity',
        description:
            'SnapCamp is building the infrastructure to expand from early regional growth into markets across all parts of the U.S.',
    },
    {
        icon: 'account-group-outline',
        title: 'Two-sided marketplace momentum',
        description:
            'The platform supports both families discovering camps and camp operators managing listings, registrations, and growth in one place.',
    },
    {
        icon: 'chart-line',
        title: 'Operational software plus discovery',
        description:
            'We are not only helping families find camps more easily, but also giving camp businesses better tools to run and scale.',
    },
];

const InvestorsScreen = () => {
    return (
        <ScrollView style={styles.page} contentContainerStyle={styles.pageContent}>
            <View style={styles.container}>
                <View style={styles.hero}>
                    <Text style={styles.eyebrow}>Investors</Text>
                    <View style={styles.heroContent}>
                        <View style={styles.heroTitleWrap}>
                            <h1 style={toHtmlTextStyle(styles.heroTitle, { marginTop: 0 })}>Help us expand SnapCamp across the United States.</h1>
                        </View>
                        <View style={styles.heroBodyWrap}>
                            <Text style={styles.heroSubtitle}>
                                We are actively looking for investors who believe in modernizing how families discover camps and how camp businesses operate, grow, and serve their communities.
                            </Text>
                        </View>
                    </View>
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>Why we are raising interest</Text>
                    <Text style={styles.sectionSubtitle}>
                        SnapCamp is preparing to grow beyond its first markets and expand into all parts of the U.S. with a product built for both discovery and camp operations.
                    </Text>

                    <View style={styles.reasonGrid}>
                        {reasons.map((reason) => (
                            <View key={reason.title} style={styles.reasonCard}>
                                <View style={styles.reasonIconWrap}>
                                    <MaterialCommunityIcons name={reason.icon as any} size={24} color="#1D4ED8" />
                                </View>
                                <Text style={styles.reasonTitle}>{reason.title}</Text>
                                <Text style={styles.reasonDescription}>{reason.description}</Text>
                            </View>
                        ))}
                    </View>
                </View>

                <View style={styles.contactCard}>
                    <Text style={styles.contactTitle}>Investor inquiries</Text>
                    <Text style={styles.contactDescription}>
                        If you are interested in investing in SnapCamp or discussing strategic expansion, reach out directly and include your name, firm, and area of interest.
                    </Text>

                    <View style={styles.contactPill}>
                        <MaterialCommunityIcons name="email-outline" size={18} color="#1D4ED8" />
                        <Text style={styles.contactEmail}>investors@snapcamp.ai</Text>
                    </View>
                </View>
            </View>
        </ScrollView>
    );
};

const styles = StyleSheet.create({
    page: {
        flex: 1,
        backgroundColor: '#FFFFFF',
    },
    pageContent: {
        paddingTop: 36,
        paddingBottom: 72,
        paddingHorizontal: PAGE_HORIZONTAL_PADDING,
    },
    container: {
        width: '100%',
    },
    hero: {
        paddingBottom: 32,
        borderBottomWidth: 1,
        borderBottomColor: '#E2E8F0',
        marginBottom: 32,
        width: '100%',
    },
    heroContent: {
        flexDirection: IS_WIDE_LAYOUT ? 'row' : 'column',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 28,
    },
    heroTitleWrap: {
        flex: IS_WIDE_LAYOUT ? 1 : undefined,
        maxWidth: IS_WIDE_LAYOUT ? 580 : undefined,
    },
    heroBodyWrap: {
        flex: IS_WIDE_LAYOUT ? 1 : undefined,
        maxWidth: IS_WIDE_LAYOUT ? 500 : undefined,
    },
    eyebrow: {
        fontSize: 13,
        lineHeight: 18,
        fontWeight: '700',
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        color: '#1D4ED8',
        marginBottom: 12,
    },
    heroTitle: {
        fontSize: 38,
        lineHeight: 46,
        fontWeight: '800',
        color: '#0F172A',
        marginBottom: 12,
    },
    heroSubtitle: {
        fontSize: 17,
        lineHeight: 28,
        color: '#475569',
    },
    section: {
        marginBottom: 36,
        width: '100%',
    },
    sectionTitle: {
        fontSize: 30,
        lineHeight: 38,
        fontWeight: '800',
        color: '#0F172A',
        marginBottom: 8,
    },
    sectionSubtitle: {
        fontSize: 16,
        lineHeight: 26,
        color: '#475569',
        marginBottom: 22,
        maxWidth: 900,
    },
    reasonGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 20,
        justifyContent: 'space-between',
    },
    reasonCard: {
        width: IS_WIDE_LAYOUT ? '31.8%' : '100%',
        borderWidth: 1,
        borderColor: '#E2E8F0',
        borderRadius: 22,
        padding: 24,
        backgroundColor: '#FFFFFF',
    },
    reasonIconWrap: {
        width: 48,
        height: 48,
        borderRadius: 16,
        backgroundColor: '#EFF6FF',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 16,
    },
    reasonTitle: {
        fontSize: 20,
        lineHeight: 28,
        fontWeight: '700',
        color: '#0F172A',
        marginBottom: 10,
    },
    reasonDescription: {
        fontSize: 15,
        lineHeight: 24,
        color: '#475569',
    },
    contactCard: {
        borderWidth: 1,
        borderColor: '#DBEAFE',
        backgroundColor: '#F8FBFF',
        borderRadius: 24,
        padding: 28,
    },
    contactTitle: {
        fontSize: 24,
        lineHeight: 32,
        fontWeight: '800',
        color: '#0F172A',
        marginBottom: 8,
    },
    contactDescription: {
        fontSize: 16,
        lineHeight: 26,
        color: '#475569',
        marginBottom: 18,
        maxWidth: 760,
    },
    contactPill: {
        alignSelf: 'flex-start',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        paddingHorizontal: 14,
        paddingVertical: 12,
        borderRadius: 999,
        backgroundColor: '#FFFFFF',
        borderWidth: 1,
        borderColor: '#BFDBFE',
    },
    contactEmail: {
        fontSize: 15,
        lineHeight: 22,
        fontWeight: '700',
        color: '#1D4ED8',
    },
});

export default InvestorsScreen;
